// dataDocuments.ts

/**
 * Interface for a single flash card item.
 */
export interface FlashCardItem {
  id: number;
  term: string;
  definition: string; // Supports HTML/JSX formatting
  confidence: number; // Percentage confidence from 0 to 100
  category: string; // Used for filtering within the document
}

/**
 * Interface for the data document container.
 */
export interface DataDocument {
  docId: number;
  title: string;
  description: string;
  cards: FlashCardItem[];
}

// --- Data Set 1: METAR Codes (Comprehensive) ---
const metarData: FlashCardItem[] = [
  // General/Rules
  { id: 1, term: 'METAR', definition: '<strong>Aviation Routine Weather Report</strong> (Observation)', confidence: 0, category: 'General' },
  { id: 2, term: 'SPECI', definition: 'Special Weather Report (<strong>unscheduled</strong> report)', confidence: 0, category: 'General' },
  { id: 3, term: 'VFR', definition: '<strong>Visual Flight Rules</strong> (Ceiling >3000ft, Visibility >5 mi)', confidence: 0, category: 'Flight Rules' },
  { id: 4, term: 'IFR', definition: '<strong>Instrument Flight Rules</strong> (Low ceiling or low visibility conditions)', confidence: 0, category: 'Flight Rules' },
  { id: 5, term: 'CAVOK', definition: '<strong>Ceiling and Visibility OK</strong>', confidence: 0, category: 'General' },

  // Intensity/Proximity
  { id: 6, term: '-', definition: '<strong>Light Intensity</strong>', confidence: 0, category: 'Modifiers' },
  { id: 7, term: '+', definition: '<strong>Heavy Intensity</strong>', confidence: 0, category: 'Modifiers' },
  { id: 8, term: 'VC', definition: 'In the <strong>Vicinity</strong> (5-10 miles from the station)', confidence: 0, category: 'Modifiers' },

  // Descriptors
  { id: 9, term: 'SH', definition: '<strong>Shower(s)</strong>', confidence: 0, category: 'Descriptors' },
  { id: 10, term: 'TS', definition: '<strong>Thunderstorm</strong>', confidence: 0, category: 'Descriptors' },
  { id: 11, term: 'FZ', definition: '<strong>Freezing</strong> (e.g., FZRA = Freezing Rain)', confidence: 0, category: 'Descriptors' },
  { id: 12, term: 'BC', definition: '<strong>Patches</strong> (e.g., BCFG = Patches of Fog)', confidence: 0, category: 'Descriptors' },
  
  // Precipitation
  { id: 16, term: 'RA', definition: '<strong>Rain</strong>', confidence: 0, category: 'Precipitation' },
  { id: 17, term: 'SN', definition: '<strong>Snow</strong>', confidence: 0, category: 'Precipitation' },
  { id: 21, term: 'GR', definition: '<strong>Hail</strong> (>5mm diameter)', confidence: 0, category: 'Precipitation' },

  // Obscuration
  { id: 25, term: 'FG', definition: '<strong>Fog</strong> (Visibility < 5/8 Statute Mile)', confidence: 0, category: 'Obscuration' },
  { id: 26, term: 'BR', definition: '<strong>Mist</strong> (Visibility 5/8SM to 6SM)', confidence: 0, category: 'Obscuration' },
  { id: 27, term: 'HZ', definition: '<strong>Haze</strong>', confidence: 0, category: 'Obscuration' },

  // Cloud Cover
  { id: 38, term: 'FEW', definition: '<strong>Few</strong> (1/8 to 2/8 coverage)', confidence: 0, category: 'Cloud Cover' },
  { id: 39, term: 'SCT', definition: '<strong>Scattered</strong> (3/8 to 4/8 coverage)', confidence: 0, category: 'Cloud Cover' },
  { id: 40, term: 'BKN', definition: '<strong>Broken</strong> (5/8 to 7/8 coverage)', confidence: 0, category: 'Cloud Cover' },
  { id: 41, term: 'OVC', definition: '<strong>Overcast</strong> (8/8 coverage / Full cloud coverage)', confidence: 0, category: 'Cloud Cover' },
];

// --- Data Set 2: Part 107 Airspace ---
const airspaceData: FlashCardItem[] = [
  { id: 101, term: 'Class G Airspace', definition: '<strong>Uncontrolled Airspace</strong>. No authorization needed for drones.', confidence: 0, category: 'Airspace Class' },
  { id: 102, term: 'Class E Airspace', definition: 'Controlled Airspace. Requires **LAANC authorization**.', confidence: 0, category: 'Airspace Class' },
  { id: 103, term: 'Class D Airspace', definition: 'Controlled Airspace around smaller airports. Requires **LAANC authorization**.', confidence: 0, category: 'Airspace Class' },
  { id: 104, term: 'Class B Airspace', definition: 'Controlled Airspace around the busiest airports (inverted wedding cake).', confidence: 0, category: 'Airspace Class' },
  { id: 105, term: 'MOA', definition: '<strong>Military Operations Area</strong>. Caution advised.', confidence: 0, category: 'Special Use' },
];

// --- Data Set 3: Part 107 Critical Weather Codes (Using the requested "Part 107 Exam" category) ---
const part107CriticalData: FlashCardItem[] = [
  { id: 201, term: 'SCT', definition: '<strong>Scattered</strong> (3/8 to 4/8 cloud coverage)', confidence: 0, category: 'Part 107 Exam' },
  { id: 202, term: 'FEW', definition: '<strong>Few</strong> (1/8 to 2/8 coverage)', confidence: 0, category: 'Part 107 Exam' },
  { id: 203, term: 'BKN', definition: '<strong>Broken</strong> (5/8 to 7/8 coverage)', confidence: 0, category: 'Part 107 Exam' },
  { id: 204, term: 'OVC', definition: '<strong>Overcast</strong> (8/8 coverage / Full cloud coverage)', confidence: 0, category: 'Part 107 Exam' },
  { id: 205, term: 'RA', definition: '<strong>Rain</strong>', confidence: 0, category: 'Part 107 Exam' },
  { id: 206, term: 'TS', definition: '<strong>Thunderstorm</strong>', confidence: 0, category: 'Part 107 Exam' },
  { id: 207, term: 'SN', definition: '<strong>Snow</strong>', confidence: 0, category: 'Part 107 Exam' },
  { id: 208, term: 'BR', definition: '<strong>Mist</strong> (Visibility 5/8SM to 6SM)', confidence: 0, category: 'Part 107 Exam' },
  { id: 209, term: 'FG', definition: '<strong>Fog</strong> (Visibility < 5/8 Statute Mile)', confidence: 0, category: 'Part 107 Exam' },
  { id: 210, term: 'RMK', definition: '<strong>Remark</strong> (Begins the remark section of the METAR)', confidence: 0, category: 'Part 107 Exam' },
  { id: 211, term: 'RAB', definition: '<strong>Rain Beginning</strong> (in the remarks section)', confidence: 0, category: 'Part 107 Exam' },
  { id: 212, term: 'RAE', definition: '<strong>Rain Ending</strong> (in the remarks section)', confidence: 0, category: 'Part 107 Exam' },

  { id: 212, term: 'RAB45', definition: '<strong>Rain Beginning at 45 min after the hr</strong> ', confidence: 0, category: 'Part 107 Exam' },
  { id: 212, term: 'RAE35', definition: '<strong>Rain Ending Ending at 35 min after the hr</strong> ', confidence: 0, category: 'Part 107 Exam' },


  { id: 212, term: 'FU', definition: 'Smoke', confidence: 0, category: 'Part 107 Exam' },
  { id: 212, term: 'FZ', definition: 'Freezing', confidence: 0, category: 'Part 107 Exam' },
  { id: 212, term: 'FG', definition: 'Fog', confidence: 0, category: 'Part 107 Exam' },
  { id: 212, term: 'CIG', definition: 'Ceiling', confidence: 0, category: 'Part 107 Exam' },
  { id: 212, term: 'BKN', definition: 'Broken - ceiling', confidence: 0, category: 'Part 107 Exam' },


  { id: 212, term: 'Burnelies principle', definition: 'Broken', confidence: 0, category: 'Part 107 Exam' },
  { id: 212, term: 'Purpuse of Flaps', definition: 'Broken', confidence: 0, category: 'Part 107 Exam' },

{ id: 212, term: 'Primary Flight controls of a fixed wing aircraft', definition: 'Broken', confidence: 0, category: 'Part 107 Exam' },



{ id: 212, term: 'True North', definition: 'Reading - use True north TAF or METAR-TEXT', confidence: 0, category: 'Part 107 Exam' },
{ id: 212, term: 'Mag North / Geo North', definition: 'ATIS, AWOS, ASOS - airport runways are layed out to magnetic north -  lines of variation- AUDIO IS MAGNETIC', confidence: 0, category: 'Part 107 Exam' },


{ id: 212, term: 'Purpuse of Flaps', definition: 'Broken', confidence: 0, category: 'Part 107 Exam' },
{ id: 212, term: 'Purpuse of Flaps', definition: 'Broken', confidence: 0, category: 'Part 107 Exam' },
{ id: 212, term: 'Purpuse of Flaps', definition: 'Broken', confidence: 0, category: 'Part 107 Exam' },
{ id: 212, term: 'Purpuse of Flaps', definition: 'Broken', confidence: 0, category: 'Part 107 Exam' },
{ id: 212, term: 'Purpuse of Flaps', definition: 'Broken', confidence: 0, category: 'Part 107 Exam' },


  



  { id: 213, term: 'Ceiling', definition: 'The ceiling is the lowest <strong>broken or overcast</strong> layer!', confidence: 0, category: 'Part 107 Exam' },
  { id: 214, term: 'Low pressure', definition: 'Bad westher', confidence: 0, category: 'Part 107 Exam' },
  { id: 215, term: 'High Pressure', definition: 'Good weather', confidence: 0, category: 'Part 107 Exam' },
  { id: 216, term: 'ZULU - FL STANDARD', definition: '- 5 hrs', confidence: 0, category: 'Part 107 Exam' },
  { id: 217, term: 'ZULU - FL Daylight Savings ', definition: '- 4hrs', confidence: 0, category: 'Part 107 Exam' },
];


const part107_TERMINAL_AERODROME_FORECAST: FlashCardItem[] = [
  { id: 501, term: 'TAF', definition: 'Terminal Aerodrome Forecast', confidence: 0, category: 'Part 107 Exam' },
  { id: 502, term: 'FM', definition: 'From modifier - expected to change at a specific date and time [ DATE - ZULU ] TIME ', confidence: 0, category: 'Part 107 Exam' },
  { id: 503, term: 'TEMPO', definition: 'Temporarily [ BEGINNING TIME ] [ ENDING TIME ] ', confidence: 0, category: 'Part 107 Exam' },

  { id: 504, term: 'PROB', definition: 'Probability - PROB 30 OR PROB 40 ( 30% OR 40% CHANCE ) ', confidence: 0, category: 'Part 107 Exam' },
  { id: 505, term: 'BECMG', definition: 'Becoming - Between these listed times -[BEGINNING TIME ] [ ENDING TIME ] - USED only on military airport ', confidence: 0, category: 'Part 107 Exam' },


  { id: 505, term: 'Low Clouds', definition: 'Surface to 6,500 feet ( Stratus, Stratocumulus, Nimbostratus) ', confidence: 0, category: 'Part 107 Exam' },

  { id: 505, term: 'Middle Clouds', definition: '6,500 to 23,000 feet  [ Altostratus, Altocumulus ]   ', confidence: 0, category: 'Part 107 Exam' },

  { id: 505, term: 'High Clouds', definition: '16,500 to 40,000 feet  [ Cirrostratus, Cirrocumulus, Cirrus ] ', confidence: 0, category: 'Part 107 Exam' },
  
  
  { id: 505, term: 'Cumuliform Clouds', definition: 'Building clouds - Thermal updrafts cause building', confidence: 0, category: 'Part 107 Exam' },
  { id: 505, term: 'Suffix - nimbus', definition: 'Rain Cloud - nimbus = rain cloud', confidence: 0, category: 'Part 107 Exam' },
  { id: 505, term: 'Cumulonimbus', definition: 'CB on TAF or METAR - Cumulo means building - nimbus = rain, Exists in unstable air', confidence: 0, category: 'Part 107 Exam' },

  { id: 505, term: 'Stratus Clouds', definition: 'Long Flat, stable conditions, resists vertical movement ', confidence: 0, category: 'Part 107 Exam' },

  { id: 505, term: 'Alto-Stratus Clouds', definition: 'Mid level, tray or blue gray in color', confidence: 0, category: 'Part 107 Exam' },



  { id: 505, term: 'VTCS', definition: 'Thunderstorms in vicinity', confidence: 0, category: 'Part 107 Exam' },
  
  
  
  { id: 505, term: 'FOG Location', definition: 'Closed to or on the ground.', confidence: 0, category: 'Part 107 Exam' },




  { id: 505, term: 'Fog 1 - Steam Fog', definition: 'Steam Fog: Cooler Air passing over a warmer surface | Water evaporates forming fog | wram water ', confidence: 0, category: 'Part 107 Exam' },
  { id: 505, term: 'Fog 2 - Uploap Fog', definition: 'Moist air flows toward sloap... fog forms on upsloap - as it goes up the sloap the air cools and creates fog', confidence: 0, category: 'Part 107 Exam' },
  
  
  { id: 505, term: 'Fog 3 - Radiation Fog', definition: 'No Wind - Heat radiating from the surface at night, cools the bottom air until it reaches saturation.  ', confidence: 0, category: 'Part 107 Exam' },
  { id: 505, term: 'Fog 4 - Advection Fog', definition: 'Little Wind - Warmer, moist air moves over a cooler surface', confidence: 0, category: 'Part 107 Exam' },

  { id: 505, term: 'Fog 5 - Presipitation Fog', definition: 'Precipitation, the precip evaporates on the way down -  cooling leads to saturation and Fog forms', confidence: 0, category: 'Part 107 Exam' },
  { id: 505, term: 'Fog 6 - Ice Fog', definition: 'Water vaper condenses on a surface that is below freezing', confidence: 0, category: 'Part 107 Exam' },

  { id: 505, term: 'Lenticular Clouds', definition: 'Strong Winds - Deadly!!', confidence: 0, category: 'Part 107 Exam' },



  { id: 505, term: 'Stratus Clouds', definition: 'Flat, stable conditions', confidence: 0, category: 'Part 107 Exam' },

  { id: 505, term: 'Stratus Clouds', definition: 'Flat, stable conditions', confidence: 0, category: 'Part 107 Exam' },
  { id: 505, term: 'Stratus Clouds', definition: 'Flat, stable conditions', confidence: 0, category: 'Part 107 Exam' },

  { id: 505, term: 'Stratus Clouds', definition: 'Flat, stable conditions', confidence: 0, category: 'Part 107 Exam' },
  { id: 505, term: 'Stratus Clouds', definition: 'Flat, stable conditions', confidence: 0, category: 'Part 107 Exam' },

  { id: 505, term: 'Stratus Clouds', definition: 'Flat, stable conditions', confidence: 0, category: 'Part 107 Exam' },
  { id: 505, term: 'Stratus Clouds', definition: 'Flat, stable conditions', confidence: 0, category: 'Part 107 Exam' },

  { id: 505, term: 'Stratus Clouds', definition: 'Flat, stable conditions', confidence: 0, category: 'Part 107 Exam' },
  { id: 505, term: 'Stratus Clouds', definition: 'Flat, stable conditions', confidence: 0, category: 'Part 107 Exam' },






];


// --- Data Set 4: Homeowners Insurance ---
const homeownersInsuranceData: FlashCardItem[] = [
    { id: 301, term: 'Dwelling Coverage (A)', definition: 'Covers the **physical structure** of your home and attached structures.', confidence: 0, category: 'Property' },
    { id: 302, term: 'Other Structures (B)', definition: 'Covers unattached structures like a **shed or detached garage**.', confidence: 0, category: 'Property' },
    { id: 303, term: 'Personal Property (C)', definition: 'Covers the **contents of your home** (furniture, clothes, electronics).', confidence: 0, category: 'Property' },
    { id: 304, term: 'Loss of Use (D)', definition: 'Covers **Additional Living Expenses (ALE)** if you must move out after a covered loss.', confidence: 0, category: 'Financial' },
    { id: 305, term: 'Liability Coverage (E)', definition: 'Covers costs if you are **found legally responsible** for injury or property damage to others.', confidence: 0, category: 'Liability' },
    { id: 306, term: 'Deductible', definition: 'The **amount you pay out-of-pocket** before your insurance begins to pay.', confidence: 0, category: 'Financial' },
    { id: 307, term: 'HO-3 Policy', definition: 'The most common policy; provides **open peril** coverage for the dwelling structure.', confidence: 0, category: 'Policy Type' },
    { id: 308, term: 'Named Peril', definition: 'A policy that only covers losses caused by the **specific disasters listed** (e.g., HO-1, HO-2).', confidence: 0, category: 'Policy Term' },
    { id: 309, term: 'Exclusion', definition: 'A **loss or risk that is specifically not covered** by the policy (e.g., flood, earthquake).', confidence: 0, category: 'Policy Term' },
];

// --- Data Set 5: Auto Insurance ---
const autoInsuranceData: FlashCardItem[] = [
    { id: 401, term: 'Bodily Injury Liability', definition: 'Covers **injuries and medical costs for others** when you are at fault.', confidence: 0, category: 'Liability' },
    { id: 402, term: 'Property Damage Liability', definition: 'Covers damage you cause to **another person’s property** (car, fence, etc.).', confidence: 0, category: 'Liability' },
    { id: 403, term: 'Collision Coverage', definition: 'Covers damage to **your own vehicle** from an accident, regardless of fault.', confidence: 0, category: 'Physical Damage' },
    { id: 404, term: 'Comprehensive Coverage', definition: 'Covers damage to your vehicle from non-collision events (**theft, fire, hail, deer**).', confidence: 0, category: 'Physical Damage' },
    { id: 405, term: 'Uninsured Motorist', definition: 'Covers **your expenses** if you are hit by a driver with no insurance.', confidence: 0, category: 'Protection' },
    { id: 406, term: 'Personal Injury Protection (PIP)', definition: 'Covers **medical expenses and lost wages** for you and your passengers, regardless of fault (no-fault states).', confidence: 0, category: 'Medical' },
    { id: 407, term: 'Gap Insurance', definition: 'Covers the **difference between what you owe** on a totaled car and its Actual Cash Value.', confidence: 0, category: 'Financial' },
];


/**
 * The full list of all available flash card documents.
 */
export const allDataDocuments: DataDocument[] = [
  { docId: 1, title: 'METAR Codes (Comprehensive)', description: 'Key abbreviations for Aviation Weather Reports, categorized by element.', cards: metarData },
  { docId: 2, title: 'Part 107: Airspace', description: 'Rules and requirements for different classes of airspace.', cards: airspaceData },
  { docId: 3, title: 'Part 107: Critical Weather Codes', description: 'Essential METAR codes required for the FAA Part 107 exam.', cards: part107CriticalData },

  { docId: 4, title: 'Homeowners Insurance', description: 'Understanding the basic coverages and financial terms.', cards: homeownersInsuranceData },
  { docId: 5, title: 'Auto Insurance', description: 'Key terms and coverages for car insurance policies.', cards: autoInsuranceData },

  { docId: 6, title: 'Part 107: TAF INFO', description: 'Essential TAF codes and info required for the FAA Part 107 exam.', cards: part107_TERMINAL_AERODROME_FORECAST },
];

// Set the initial data to be the first document's cards
export const initialDocument = allDataDocuments[0];